import { Box, Chip, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../components/ui/Section';
import { CustomerIntakeForm } from '../types/CustomerIntakeForm';

interface OnboardingReviewProps {
  formData: CustomerIntakeForm;
}

const SummaryRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.5, fontWeight: 500 }}>
        {label}
      </Box>
      <Typography variant="textSM">{value || '-'}</Typography>
    </Box>
  );
};

const TagList: React.FC<{ label: string; values: string[] }> = ({ label, values }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.5, fontWeight: 500 }}>
        {label}
      </Box>
      {values.length === 0
        ? <Typography variant="textSM"
          color="text.secondary">-</Typography>
        : (
          <Stack direction="row"
            flexWrap="wrap"
            gap={1}>
            {values.map((value) => (
              <Chip key={value}
                label={value}
                size="small" />
            ))}
          </Stack>
        )}
    </Box>
  );
};

const OnboardingReview: React.FC<OnboardingReviewProps> = ({ formData }) => {
  const { t } = useTranslation('common');

  return (
    <Box sx={{ p: 6 }}>
      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mb: 4, display: 'block' }}>
        {t('onboardingIntake.reviewHelp', 'Review the captured onboarding details before creating the client record.')}
      </Typography>

      <Grid container
        spacing={6}>
        <Grid item
          xs={12}
          md={6}>
          <Section style={{ p: 4, mb: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 3, display: 'block' }}>
              {t('onboardingIntake.reviewProfileTitle', 'Client profile')}
            </Typography>
            <SummaryRow label={t('onboardingIntake.customerName', 'Client name')}
              value={formData.name} />
            <SummaryRow label={t('onboardingIntake.sector', 'Sector')}
              value={formData.sector} />
            <SummaryRow label={t('onboardingIntake.operatingModel', 'Operating model')}
              value={formData.operatingModel} />
            <SummaryRow label={t('onboardingIntake.accountOwner', 'Account owner')}
              value={formData.owner} />
            <SummaryRow label={t('onboardingIntake.sitesCount', 'Number of locations')}
              value={formData.sitesCount} />
            <SummaryRow label={t('onboardingIntake.targetGoLive', 'Target go-live date')}
              value={formData.targetGoLive} />
          </Section>

          <Section style={{ p: 4, mb: 0 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 3, display: 'block' }}>
              {t('onboardingIntake.reviewCommercialTitle', 'Commercial & compliance')}
            </Typography>
            <SummaryRow label={t('onboardingIntake.budget', 'Budget')}
              value={formData.budget} />
            <SummaryRow label={t('onboardingIntake.pricingModel', 'Pricing model')}
              value={formData.pricingModel} />
            <TagList label={t('onboardingIntake.complianceExpectations', 'Compliance expectations')}
              values={formData.complianceExpectations} />
            <TagList label={t('onboardingIntake.reportingRequirements', 'Reporting requirements')}
              values={formData.reportingRequirements} />
          </Section>
        </Grid>

        <Grid item
          xs={12}
          md={6}>
          <Section style={{ p: 4, mb: 0 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 3, display: 'block' }}>
              {t('onboardingIntake.reviewServiceTitle', 'Service & catering requirements')}
            </Typography>
            <TagList label={t('onboardingIntake.userTypes', 'Type of users')}
              values={formData.userTypes} />
            <TagList label={t('onboardingIntake.serviceTimes', 'Service times')}
              values={formData.serviceTimes} />
            <TagList label={t('onboardingIntake.menus', 'Menus')}
              values={formData.menus} />
            <TagList label={t('onboardingIntake.dietaryRequirements', 'Dietary requirements')}
              values={formData.dietaryRequirements} />
            <TagList label={t('onboardingIntake.allergens', 'Allergens to accommodate')}
              values={formData.allergens} />
            <SummaryRow label={t('onboardingIntake.hospitalityNeeds', 'Hospitality needs')}
              value={formData.hospitalityNeeds} />
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OnboardingReview;
